import { isBlockEnabled } from "@/src/lib/blocks/utils";
import { getNestedValue, hasValue } from "@/src/lib/utils/object-utils";
import { type BlockConfigFile } from "@/src/models/blocks/block-config";
import { BlockKey, BlockType, getBlockTypeFromKey } from "@/src/models/blocks/block-types";
import { getBlockKeysForClassification } from "@/src/models/blocks/enriched-content";
import { type File } from "@/src/models/file";
import { type Section } from "@/src/models/section";

export const AutoIntakeEligibilityRequirement = {
  NO_CROPPED_PAGES: "no_cropped_pages",
  MATCHED_PROVIDER_NPI: "matched_provider_npi",
  GEOCODED_LOCATION: "geocoded_location",
  PATIENT_PHONE_NUMBER: "patient_phone_number",
} as const;

export type AutoIntakeEligibilityRequirement =
  (typeof AutoIntakeEligibilityRequirement)[keyof typeof AutoIntakeEligibilityRequirement];
export type AutoIntakeEligibilityRequirements = readonly AutoIntakeEligibilityRequirement[];

type HasValueAtPath = (path: string) => boolean;

interface FileEligibilityRequirementRule {
  scope: "file";
  failureReason: string;
  passes: (file: Pick<File, "croppedPageIndices"> | null | undefined) => boolean;
}

interface BlockEligibilityRequirementRule {
  scope: "block";
  blockType: BlockType;
  failureReason: string;
  passes: (has: HasValueAtPath) => boolean;
}

type AutoIntakeEligibilityRequirementRule = FileEligibilityRequirementRule | BlockEligibilityRequirementRule;

const AUTO_INTAKE_ELIGIBILITY_REQUIREMENT_RULES = {
  [AutoIntakeEligibilityRequirement.NO_CROPPED_PAGES]: {
    scope: "file",
    failureReason: "cropped_pages",
    passes: (file) => (file?.croppedPageIndices?.length ?? 0) === 0,
  },
  [AutoIntakeEligibilityRequirement.MATCHED_PROVIDER_NPI]: {
    scope: "block",
    blockType: BlockType.PROVIDER,
    failureReason: "provider_npi_not_matched",
    passes: (has) => has("npiNumber"),
  },
  [AutoIntakeEligibilityRequirement.GEOCODED_LOCATION]: {
    scope: "block",
    blockType: BlockType.LOCATION,
    failureReason: "location_not_geocoded",
    passes: (has) => has("placeId") && has("address.line1"),
  },
  [AutoIntakeEligibilityRequirement.PATIENT_PHONE_NUMBER]: {
    scope: "block",
    blockType: BlockType.PATIENT,
    failureReason: "phone_number_missing",
    passes: (has) => has("mobilePhoneNumber") || has("homePhoneNumber"),
  },
} as const satisfies Record<AutoIntakeEligibilityRequirement, AutoIntakeEligibilityRequirementRule>;

type EligibilityRequirementRule =
  (typeof AUTO_INTAKE_ELIGIBILITY_REQUIREMENT_RULES)[keyof typeof AUTO_INTAKE_ELIGIBILITY_REQUIREMENT_RULES];
export type FileEligibilityFailureReason = Extract<EligibilityRequirementRule, { scope: "file" }>["failureReason"];
export type BlockEligibilityFailureReason = Extract<EligibilityRequirementRule, { scope: "block" }>["failureReason"];

export type FileEligibilityRequirementResult = { ok: true } | { ok: false; reason: FileEligibilityFailureReason };

export type BlockEligibilityRequirementResult =
  | { ok: true }
  | { ok: false; blockKey: BlockKey; reason: BlockEligibilityFailureReason };

// Returns the first file-level eligibility requirement that fails, else { ok: true }.
export function checkFileEligibilityRequirements(
  file: Pick<File, "croppedPageIndices"> | null | undefined,
  requirements: AutoIntakeEligibilityRequirements
): FileEligibilityRequirementResult {
  for (const requirement of requirements) {
    const rule = AUTO_INTAKE_ELIGIBILITY_REQUIREMENT_RULES[requirement];

    if (rule.scope === "file" && !rule.passes(file)) {
      return { ok: false, reason: rule.failureReason };
    }
  }

  return { ok: true };
}

// Returns the first enabled block that fails its eligibility requirement, else { ok: true }.
export function checkBlockEligibilityRequirements(
  section: Pick<Section, "classification" | "enrichedContent">,
  blockConfig: BlockConfigFile | undefined | null,
  requirements: AutoIntakeEligibilityRequirements
): BlockEligibilityRequirementResult {
  const enrichedContent = section.enrichedContent as Record<BlockKey, { initialReviewedFields?: unknown }> | null;

  for (const blockKey of getBlockKeysForClassification(section.classification)) {
    if (!isBlockEnabled(blockConfig, blockKey)) {
      continue;
    }

    const initialReviewedFields = enrichedContent?.[blockKey]?.initialReviewedFields;
    const has = (path: string): boolean => hasValue(getNestedValue(initialReviewedFields, path));
    const blockType = getBlockTypeFromKey(blockKey);

    for (const requirement of requirements) {
      const rule = AUTO_INTAKE_ELIGIBILITY_REQUIREMENT_RULES[requirement];

      if (rule.scope === "block" && rule.blockType === blockType && !rule.passes(has)) {
        return { ok: false, blockKey, reason: rule.failureReason };
      }
    }
  }

  return { ok: true };
}
